import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X, ShoppingCart, Trash2, Minus, Plus, Package, Lock, Loader2, Store, Truck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../lib/useCart";
import { useSettings } from "../../lib/useSettings";
import { useCustomerAuth } from "../../lib/useCustomerAuth";
import PickupPicker from "./PickupPicker";
import JOCartProSection from "../jardins-ole/JOCartProSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSTOMER_TOKEN_KEY = "msc_customer_token";

function priceFmt(cents, symbol = "€") {
  return `${(cents / 100).toFixed(2)} ${symbol}`;
}

export default function CartDrawer() {
  const { items, count, subtotal_cents, remove, setQty, clear, drawerOpen, setDrawerOpen } = useCart();
  const { settings: s } = useSettings();
  const { customer } = useCustomerAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [mode, setMode] = useState("shipping"); // "shipping" | "pickup"
  const [pickup, setPickup] = useState(null); // { location_id, location_name, slot_at, label }
  const [showPickupPicker, setShowPickupPicker] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [shippingModeId, setShippingModeId] = useState("");
  const shippingModes = (s?.shipping_modes || []).filter(m => m.enabled !== false);
  // Estimation des frais d'envoi côté client (mise à jour après redirection Stripe = backend recalcule)
  const cartWeightG = items.reduce((sum, i) => sum + (i.weight_g || 0) * (i.qty || 1), 0);
  const selectedShippingMode = shippingModes.find(m => m.id === shippingModeId);
  const estimateShippingCents = (m) => {
    if (!m) return 0;
    if (m.free_from_cents && subtotal_cents >= m.free_from_cents) return 0;
    const tiers = [...(m.weight_tiers || [])].sort((a, b) => (a.max_g || 0) - (b.max_g || 0));
    for (const t of tiers) if (cartWeightG <= (t.max_g || 999999)) return t.price_cents || 0;
    return tiers.length ? tiers[tiers.length - 1].price_cents || 0 : 0;
  };
  const shippingEstimateCents = selectedShippingMode ? estimateShippingCents(selectedShippingMode) : 0;

  if (!s?.shop_enabled) return null;
  const ccEnabled = !!s?.shop_click_collect_enabled;
  const shopStatus = s?.shop_status || "online";
  const checkoutBlocked = shopStatus !== "online";
  const cgvRequired = s?.cgv_required_checkout !== false;
  const cgvLabel = s?.cgv_checkbox_label || "J'ai lu et j'accepte les conditions générales de vente";
  const blockedReason = {
    catalog: "Mode catalogue actif — commandes par téléphone uniquement.",
    vacation: "Nous sommes en congés, les commandes reprendront bientôt.",
    maintenance: "Boutique en maintenance, réessayez dans quelques minutes.",
    draft: "Boutique en brouillon — visible pour vous (admin).",
  }[shopStatus];

  const currency = s?.shop_currency_symbol || "€";

  const startCheckout = async () => {
    if (items.length === 0) return;
    if (mode === "pickup" && !pickup) {
      toast.error("Choisissez un point de retrait et un créneau");
      setShowPickupPicker(true);
      return;
    }
    if (cgvRequired && !cgvAccepted) {
      toast.error("Vous devez accepter les CGV pour continuer");
      return;
    }
    setCheckingOut(true);
    try {
      const token = localStorage.getItem(CUSTOMER_TOKEN_KEY) || "";
      const payload = {
        items: items.map(i => ({ product_id: i.id, qty: i.qty, variant_id: i.variant_id || undefined })),
        origin_url: window.location.origin,
        customer_email: customer?.email || undefined,
        fulfillment_type: mode,
        cgv_accepted: cgvAccepted,
      };
      if (mode === "pickup" && pickup) {
        payload.pickup_location_id = pickup.location_id;
        payload.pickup_slot_at = pickup.slot_at;
      }
      if (mode === "shipping" && shippingModeId) {
        payload.shipping_mode_id = shippingModeId;
      }
      const { data } = await axios.post(
        `${API}/checkout/create-session`,
        payload,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!data?.url) throw new Error("URL Stripe manquante");
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible de démarrer le paiement. Réessayez.");
      setCheckingOut(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] animate-in fade-in duration-200"
          onClick={() => setDrawerOpen(false)}
          data-testid="cart-overlay"
        />
      )}

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-white z-[61] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!drawerOpen}
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2.5">
            <ShoppingCart className="w-5 h-5 text-[color:var(--brand-navy)]" />
            <h2 className="font-display font-bold text-lg text-[color:var(--brand-navy)]">Mon panier</h2>
            {count > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)]">
                {count} {count > 1 ? "articles" : "article"}
              </span>
            )}
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="w-9 h-9 rounded-full hover:bg-[hsl(var(--muted))] flex items-center justify-center transition-colors"
            aria-label="Fermer"
            data-testid="cart-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto" data-testid="cart-items">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--muted))]/40 flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-[hsl(var(--muted-foreground))]/60" />
              </div>
              <div className="font-display font-bold text-[color:var(--brand-navy)]">Votre panier est vide</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-xs">
                Ajoutez des produits pour les retrouver ici — ils seront sauvegardés pour votre prochaine visite.
              </p>
              <Link
                to="/shop"
                onClick={() => setDrawerOpen(false)}
                className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 text-white text-sm font-semibold transition"
                data-testid="cart-empty-shop-cta"
              >
                Parcourir la boutique
              </Link>
            </div>
          ) : (
            <ul className="divide-y">
              {items.map(p => (
                <li key={p.line_key || p.id} className="p-4 flex gap-3" data-testid={`cart-item-${p.slug}`}>
                  <Link to={`/shop/${p.slug}`} onClick={() => setDrawerOpen(false)} className="shrink-0">
                    <div className="w-20 h-20 rounded-lg bg-[hsl(var(--muted))]/30 overflow-hidden border">
                      {p.featured_image ? (
                        <img src={p.featured_image} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-[hsl(var(--muted-foreground))]/40" /></div>
                      )}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/shop/${p.slug}`}
                      onClick={() => setDrawerOpen(false)}
                      className="font-semibold text-sm text-[color:var(--brand-navy)] hover:text-[color:var(--brand-sky)] line-clamp-2"
                    >
                      {p.name}
                    </Link>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{priceFmt(p.price_cents, currency)} · l'unité</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 border rounded-full overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setQty(p.line_key || p.id, p.qty - 1)}
                          disabled={p.qty <= 1}
                          className="w-7 h-7 flex items-center justify-center hover:bg-[hsl(var(--muted))] disabled:opacity-30 transition"
                          aria-label="Diminuer"
                          data-testid={`cart-qty-minus-${p.slug}`}
                        ><Minus className="w-3 h-3" /></button>
                        <span className="min-w-[1.8rem] text-center text-sm font-semibold font-mono tabular-nums" data-testid={`cart-qty-${p.slug}`}>{p.qty}</span>
                        <button
                          type="button"
                          onClick={() => setQty(p.line_key || p.id, p.qty + 1)}
                          disabled={p.stock_tracking && p.qty >= (p.max_qty || 99)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-[hsl(var(--muted))] disabled:opacity-30 transition"
                          aria-label="Augmenter"
                          data-testid={`cart-qty-plus-${p.slug}`}
                        ><Plus className="w-3 h-3" /></button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[color:var(--brand-navy)] text-sm">{priceFmt(p.price_cents * p.qty, currency)}</span>
                        <button
                          type="button"
                          onClick={() => remove(p.line_key || p.id)}
                          className="w-7 h-7 flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-red-600 transition"
                          aria-label="Retirer"
                          data-testid={`cart-remove-${p.slug}`}
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pickup picker panel (overlay inside drawer) */}
        {showPickupPicker && (
          <div className="absolute inset-0 bg-white z-10 flex flex-col p-5" data-testid="pickup-picker-panel">
            <button type="button" onClick={() => setShowPickupPicker(false)} className="self-start inline-flex items-center gap-1 text-xs font-bold text-[color:var(--brand-navy)] mb-3 hover:underline">
              <ArrowLeft className="w-3.5 h-3.5" />Retour au panier
            </button>
            <PickupPicker
              maxDaysAhead={s?.pickup_max_days_ahead || 14}
              onPick={(p) => { setPickup(p); setShowPickupPicker(false); toast.success(`Créneau ${p.label} · ${p.location_name}`); }}
              onCancel={() => setShowPickupPicker(false)}
            />
          </div>
        )}

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t p-5 bg-[hsl(var(--muted))]/10 space-y-3">
            {/* Mode toggle (livraison vs C&C) */}
            {ccEnabled && (
              <div className="grid grid-cols-2 gap-1.5 p-1 bg-[hsl(var(--muted))]/40 rounded-full" data-testid="cart-mode-toggle">
                <button
                  type="button"
                  onClick={() => { setMode("shipping"); setPickup(null); }}
                  className={`py-2 rounded-full text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-all ${mode === "shipping" ? "bg-white shadow text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))]"}`}
                  data-testid="cart-mode-shipping"
                ><Truck className="w-3.5 h-3.5" />Livraison</button>
                <button
                  type="button"
                  onClick={() => { setMode("pickup"); if (!pickup) setShowPickupPicker(true); }}
                  className={`py-2 rounded-full text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-all ${mode === "pickup" ? "bg-white shadow text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))]"}`}
                  data-testid="cart-mode-pickup"
                ><Store className="w-3.5 h-3.5" />Click & Collect</button>
              </div>
            )}
            {/* Shipping mode selector (Phase 14) */}
            {mode === "shipping" && shippingModes.length > 0 && (
              <div className="space-y-1.5" data-testid="cart-shipping-modes">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Mode de livraison</div>
                {shippingModes.map(m => {
                  const cost = estimateShippingCents(m);
                  const isFree = m.free_from_cents && subtotal_cents >= m.free_from_cents;
                  const selected = shippingModeId === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setShippingModeId(m.id)}
                      className={`w-full p-2.5 rounded-lg border text-left transition ${selected ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/10" : "border-[hsl(var(--border))] hover:border-[color:var(--brand-sky)]/50"}`}
                      data-testid={`shipping-mode-${m.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-[color:var(--brand-navy)]">{m.name}</span>
                        <span className="text-xs font-bold tabular-nums">
                          {isFree ? <span className="text-emerald-600">Offert</span> : cost === 0 ? "—" : priceFmt(cost, currency)}
                        </span>
                      </div>
                      {m.eta_label && <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">{m.eta_label}</div>}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Pickup summary if chosen */}
            {mode === "pickup" && pickup && (
              <button
                type="button"
                onClick={() => setShowPickupPicker(true)}
                className="w-full p-3 rounded-xl bg-[color:var(--brand-sky)]/8 border border-[color:var(--brand-sky)]/30 text-left hover:bg-[color:var(--brand-sky)]/15 transition"
                data-testid="cart-pickup-summary"
              >
                <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--brand-sky)]">Retrait prévu</div>
                <div className="text-sm font-bold text-[color:var(--brand-navy)] mt-0.5 line-clamp-1">{pickup.location_name}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                  {new Date(pickup.slot_at).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
                  <span className="text-[color:var(--brand-sky)] font-bold ml-2">Modifier</span>
                </div>
              </button>
            )}
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-[hsl(var(--muted-foreground))]">Sous-total</span>
              <span className="text-2xl font-bold text-[color:var(--brand-navy)]" data-testid="cart-subtotal">{priceFmt(subtotal_cents, currency)}</span>
            </div>
            {mode === "shipping" && selectedShippingMode && (
              <div className="flex items-baseline justify-between text-sm" data-testid="cart-shipping-cost">
                <span className="text-[hsl(var(--muted-foreground))]">Livraison ({selectedShippingMode.name})</span>
                <span className="font-bold tabular-nums">
                  {shippingEstimateCents === 0 ? <span className="text-emerald-600">Offerte</span> : priceFmt(shippingEstimateCents, currency)}
                </span>
              </div>
            )}
            {mode === "shipping" && selectedShippingMode && (shippingEstimateCents > 0 || shippingEstimateCents === 0) && (
              <div className="flex items-baseline justify-between pt-2 border-t">
                <span className="text-xs font-bold uppercase tracking-wider text-[color:var(--brand-navy)]">Total estimé</span>
                <span className="text-xl font-bold text-[color:var(--brand-navy)] tabular-nums" data-testid="cart-total-estimate">{priceFmt(subtotal_cents + shippingEstimateCents, currency)}</span>
              </div>
            )}
            {s?.shop_tax_included && (
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">TTC · {mode === "pickup" ? "Retrait sans frais" : selectedShippingMode ? "Total définitif recalculé à l'étape suivante" : "Choisissez un mode de livraison"}</div>
            )}

            {/* Pro account — credit limit + 30j fin de mois (Jardins d'Éole CRM) */}
            <JOCartProSection />

            {/* CGV checkbox */}
            {cgvRequired && !checkoutBlocked && (
              <label className="flex items-start gap-2.5 p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 text-[11px] cursor-pointer hover:border-[color:var(--brand-sky)]/50 transition" data-testid="cart-cgv-wrapper">
                <input
                  type="checkbox"
                  checked={cgvAccepted}
                  onChange={(e) => setCgvAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[color:var(--brand-sky)] cursor-pointer shrink-0"
                  data-testid="cart-cgv-checkbox"
                />
                <span className="text-[hsl(var(--foreground))] leading-snug">
                  {cgvLabel.split(/\s/).filter(Boolean).length > 0 ? cgvLabel : "J'accepte les CGV"}
                  <Link to="/cgv" target="_blank" rel="noopener noreferrer" className="ml-1 underline font-semibold text-[color:var(--brand-sky)] hover:text-[color:var(--brand-navy)]" data-testid="cart-cgv-link" onClick={(e) => e.stopPropagation()}>Lire les CGV</Link>
                  <span className="mx-1 opacity-40">·</span>
                  <Link to="/livraison-retours" target="_blank" rel="noopener noreferrer" className="underline font-semibold text-[color:var(--brand-sky)] hover:text-[color:var(--brand-navy)]" onClick={(e) => e.stopPropagation()}>Livraison & Retours</Link>
                </span>
              </label>
            )}

            <button
              type="button"
              onClick={startCheckout}
              disabled={checkingOut || checkoutBlocked || (cgvRequired && !cgvAccepted)}
              className="w-full h-12 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
              data-testid="cart-checkout"
              title={checkoutBlocked ? blockedReason : (cgvRequired && !cgvAccepted ? "Cochez la case CGV pour continuer" : "")}
            >
              {checkingOut ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Redirection Stripe…</>
              ) : checkoutBlocked ? (
                <><Lock className="w-4 h-4" />Indisponible</>
              ) : (
                <><Lock className="w-4 h-4" />Passer au paiement</>
              )}
            </button>
            {checkoutBlocked && (
              <p className="text-[11px] text-center text-[color:var(--brand-red)] font-semibold" data-testid="checkout-blocked-msg">{blockedReason}</p>
            )}
            <div className="flex items-center justify-between pt-2 border-t text-xs">
              <button
                type="button"
                onClick={() => { if (window.confirm("Vider tout le panier ?")) clear(); }}
                className="text-[hsl(var(--muted-foreground))] hover:text-red-600 transition"
                data-testid="cart-clear"
              >
                Vider le panier
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="font-semibold text-[color:var(--brand-sky)] hover:underline"
              >
                Continuer mes achats
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
